import {
  Box,
  Button,
  Text,
  Flex,
  useDisclosure,
  Spinner,
  Portal,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import SearchInput from "@root/components/common/SearchInput";
import {
  CRUD_TYPE_ENUM,
  DELETE,
  EDIT,
  PAGINATION_DEFAULTS,
} from "@root/constants";
import { DataTable } from "@root/components";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ModalDelete } from "@root/components/modal-delete";
import { PlusIcon } from "@root/assets/admin/icons/plusicon";
import { TableColumns } from "./subSubscriber-tablecolumns";
import { AddSubscriberModal } from "./AddSubscriberModal";
import { useSubSubscriberManagement } from "@root/hooks/useSubsubcriber";
import { ProfileChangeHistoryDrawer } from "@root/components/common/drawer/ProfileChangeHistoryDrawer";
import { useGlobalContext } from "@root/context/global-context";
import { usePledgeSubSubscriberManagementTourGuide } from "@root/hooks/usePledgeSubSubscriberManagementTourGuide";
import { TourGuide, RestartTourButton } from "@root/components/tour-guide";

const SubsubscriberManagement = () => {
  const { t } = useTranslation("common");
  const { pledgeSubSubscriberManagementTourGuide, isMobile: isMobileTour } =
    usePledgeSubSubscriberManagementTourGuide();
  const [search, setSearch] = useState("");
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [selectedSubsubscriber, setSelectedSubsubscriber] = useState<any>(null);
  const [isViewHistoryOpen, setIsViewHistoryOpen] = useState(false);
  const [action, setAction] = useState(CRUD_TYPE_ENUM.DEFAULT);
  const { getChatHistory, userHistory, historyPage } = useGlobalContext();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // ✅ Disclosure for Add/Edit modal
  const {
    isOpen: isAddModalOpen,
    onOpen: openAddModal,
    onClose: closeAddModal,
  } = useDisclosure();

  const {
    fetchSubsubscriber,
    subsubscriberData,
    addSubsubscriber,
    deleteSubsubscriber,
    page,
    setPage,
    pageCount,
    subsubscriberlLoading,
  }: any = useSubSubscriberManagement();

  const navigate = useNavigate();

  const handleChangeHistoryClick = async (id: any) => {
    setSelectedUserId(id); // Store the ID
    const payload = {
      id: id,
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      offset: (historyPage - 1) * PAGINATION_DEFAULTS.PAGESIZE,
    };
    try {
      await getChatHistory(payload);
      setIsViewHistoryOpen(true);
    } catch (error) {
      // Error fetching user history
    }
  };

  // Handle table actions
  function handleActions(type: string, rowData: any) {
    // if (type === EDIT) {
    //   setAction(CRUD_TYPE_ENUM.UPDATE);
    //   setSelectedSubsubscriber(rowData);
    //   openAddModal();
    // }
    if (type === DELETE) {
      setSelectedSubsubscriber(rowData);
      setIsDeleteModal(true);
    }
  }

  // ✅ Add or Update subscriber
  async function handleAddSubscriber(name: string, email: string) {
    const payload = { fullName: name, email };

    try {
      await addSubsubscriber(payload);
    } catch {
      // Error toast already shown by the hook — keep the modal open so the
      // user can correct the input.
      return;
    }
    closeAddModal();
    setSelectedSubsubscriber(null);
    setAction(CRUD_TYPE_ENUM.DEFAULT);

    // Refresh list after adding/updating
    fetchSubsubscriber({
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      offset: (page - 1) * PAGINATION_DEFAULTS.PAGESIZE,
      search,
    });
  }

  // ✅ Delete subscriber confirm
  const handleDeleteConfirm = async () => {
    if (selectedSubsubscriber) {
      await deleteSubsubscriber(selectedSubsubscriber.id);
      setIsDeleteModal(false);
      setSelectedSubsubscriber(null);
      fetchSubsubscriber({
        limit: PAGINATION_DEFAULTS.PAGESIZE,
        offset: (page - 1) * PAGINATION_DEFAULTS.PAGESIZE,
        search,
      });
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModal(false);
    setSelectedSubsubscriber(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  useEffect(() => {
    const payload = {
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      offset: (page - 1) * PAGINATION_DEFAULTS.PAGESIZE,
      search,
    };
    fetchSubsubscriber(payload);
  }, [page, search]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  return (
    <Flex direction="column" height="100%" bg="white" p="24px 16px">
      {/* Header */}
      <Box display="flex" justifyContent="space-between">
        <Box>
          <Text fontWeight={600} color="#2F2F34" fontSize="24px" mb="10px">
            {t("subsubscriberManagement.title")}
          </Text>
          <Text color="#798692" maxWidth="600px">
            {t("subsubscriberManagement.subtitle")}
          </Text>
        </Box>
        <Button
          data-tour-item="pledge-subsubscriber-add"
          bgColor="#1819B4"
          color="white"
          fontWeight="medium"
          onClick={() => {
            setAction(CRUD_TYPE_ENUM.ADD);
            setSelectedSubsubscriber(null);
            openAddModal();
          }}
          minW="fit-content"
          px={4}
          whiteSpace="nowrap"
        >
          <PlusIcon style={{ width: "24px", height: "24px", padding: "5px" }} />
          {t("subsubscriberManagement.addNewSubscriber")}
        </Button>
      </Box>

      {/* Add/Edit Subscriber Modal */}
      <AddSubscriberModal
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        handleSubmit={handleAddSubscriber}
        selectedSubSubscriber={selectedSubsubscriber}
        action={action}
      />

      {/* Search Bar */}
      <Flex justifyContent="space-between" my={10}>
        <SearchInput
          data-tour-item="pledge-subsubscriber-search"
          placeholder={t("subsubscriberManagement.search")}
          width="550px"
          value={search}
          onChange={handleSearchChange}
        />
      </Flex>
      <ProfileChangeHistoryDrawer
        isOpen={isViewHistoryOpen}
        onClose={() => setIsViewHistoryOpen(false)}
        viewHistoryChange={userHistory}
      />

      {/* Data Table */}
      <Box data-tour-item="pledge-subsubscriber-table">
        {subsubscriberlLoading ? (
          <Flex
            flexDir={"column"}
            justifyContent={"center"}
            alignItems={"center"}
            height={"350px"}
            gap={3}
          >
            <Spinner size="lg" color="#1819B4" />
          </Flex>
        ) : (
          <DataTable
            columns={TableColumns(handleActions, t, handleChangeHistoryClick)}
            data={subsubscriberData || []}
            showPagination={true}
            page={page}
            pageCount={pageCount}
            pageSize={PAGINATION_DEFAULTS.PAGESIZE}
            handlePageChange={handlePageChange}
          />
        )}
      </Box>

      {!isMobileTour && !pledgeSubSubscriberManagementTourGuide.isActive && (
        <Portal>
          <RestartTourButton
            onClick={pledgeSubSubscriberManagementTourGuide.startTour}
            position={{ bottom: "24px", right: "24px" }}
          />
        </Portal>
      )}
      {!isMobileTour &&
        pledgeSubSubscriberManagementTourGuide.isActive &&
        pledgeSubSubscriberManagementTourGuide.currentStepData && (
          <TourGuide
            isActive={pledgeSubSubscriberManagementTourGuide.isActive}
            currentStep={pledgeSubSubscriberManagementTourGuide.currentStepData}
            currentStepIndex={
              pledgeSubSubscriberManagementTourGuide.currentStep
            }
            totalSteps={pledgeSubSubscriberManagementTourGuide.totalSteps}
            onNext={pledgeSubSubscriberManagementTourGuide.nextStep}
            onBack={pledgeSubSubscriberManagementTourGuide.previousStep}
            onClose={pledgeSubSubscriberManagementTourGuide.stopTour}
            targetSelector={
              pledgeSubSubscriberManagementTourGuide.currentStepData
                .targetSelector
            }
            borderRadius="8px"
          />
        )}

      {/* Delete Confirmation Modal */}
      <ModalDelete
        isOpen={isDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("faq.confirmDelete")}
        description={t("courses.deleteDescription")}
        deleteButtonTitle={t("faq.delete")}
        cancelButtonTitle={t("faq.cancel")}
      />
    </Flex>
  );
};

export default SubsubscriberManagement;
